import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import ExplorerShell from "../components/explorer/ExplorerShell";
import {
  CategoryExplorerCard,
  CategoryExplorerListRow,
} from "../components/explorer/CategoryExplorerViews";
import { useExplorerUrlState } from "../hooks/useExplorerUrlState";
import { apiService } from "../api/services/apiService";
import type { Category, ProductCatalogItem } from "../types";
import {
  collectUniqueTags,
  compareExplorerValues,
  isVisibleToExplorer,
  matchesExplorerSearch,
  matchesTagFilter,
  paginate,
  EXPLORER_PAGE_SIZE_CARD,
  EXPLORER_PAGE_SIZE_LIST,
} from "../utils/explorerUtils";

const CATEGORY_SORT_OPTIONS = [
  { id: "name", label: "Name" },
  { id: "products", label: "Product count" },
  { id: "status", label: "Status" },
];

interface CategoryWithCount extends Category {
  productCount: number;
}

const accordionSx = {
  mt: 2,
  border: "1px solid",
  borderColor: "grey.200",
  "&:before": { display: "none" },
} as const;

const CategoryExplorerPage: React.FC = () => {
  const state = useExplorerUrlState({ defaultSort: "name" });
  const [categories, setCategories] = useState<Category[]>([]);
  const [catalog, setCatalog] = useState<ProductCatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [categoryList, productCatalog] = await Promise.all([
        apiService.getCategories(),
        apiService.getProductCatalog(),
      ]);
      setCategories(categoryList.filter((c) => isVisibleToExplorer(c.status)));
      setCatalog(productCatalog);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Failed to load categories",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const productCountByCategoryName = useMemo(() => {
    const counts = new Map<string, number>();
    for (const product of catalog) {
      const names =
        product.categoryNames?.length > 0 ? product.categoryNames : [];
      for (const name of names) {
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }
    return counts;
  }, [catalog]);

  const categoriesWithCounts = useMemo(
    (): CategoryWithCount[] =>
      categories.map((category) => ({
        ...category,
        productCount: productCountByCategoryName.get(category.name) ?? 0,
      })),
    [categories, productCountByCategoryName],
  );

  const tagFilterOptions = useMemo(
    () => collectUniqueTags(categoriesWithCounts),
    [categoriesWithCounts],
  );

  const processed = useMemo(() => {
    let list: CategoryWithCount[] = categoriesWithCounts;

    if (state.statuses.length) {
      list = list.filter((c) =>
        state.statuses.includes(c.status as (typeof state.statuses)[number]),
      );
    }

    list = list.filter((c) => matchesTagFilter(c, state.tags));

    list = list.filter((c) =>
      matchesExplorerSearch(state.debouncedQ, [
        c.name,
        c.description,
        ...(c.tags ?? []),
      ]),
    );

    list = [...list].sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (state.sort) {
        case "products":
          av = a.productCount;
          bv = b.productCount;
          break;
        case "status":
          av = a.status;
          bv = b.status;
          break;
        default:
          av = a.name;
          bv = b.name;
      }
      return compareExplorerValues(av, bv, state.sortDir);
    });

    return list;
  }, [
    categoriesWithCounts,
    state.debouncedQ,
    state.sort,
    state.sortDir,
    state.statuses,
    state.tags,
  ]);

  const pageSize =
    state.layout === "list"
      ? EXPLORER_PAGE_SIZE_LIST
      : EXPLORER_PAGE_SIZE_CARD;
  const paginated = paginate(processed, state.page, pageSize);

  const activeFilterChips = state.tags.map((tag) => (
    <Chip
      key={`tag-${tag}`}
      label={tag}
      size="small"
      color="primary"
      variant="outlined"
      onDelete={() => state.toggleTag(tag)}
      sx={{ mr: 0.5, mb: 1 }}
    />
  ));

  return (
    <ExplorerShell
      pageTitle="Category Explorer"
      pageDescription="Browse product categories. Filter by tag, status, or search."
      loading={loading}
      error={error}
      items={paginated}
      totalFiltered={processed.length}
      state={state}
      sortOptions={CATEGORY_SORT_OPTIONS}
      hasExtraFilters={state.tags.length > 0}
      activeFilterChips={activeFilterChips}
      emptyMessage="No categories are available to explore."
      getItemKey={(c) => c.uuid}
      renderCard={(c) => (
        <CategoryExplorerCard category={c} productCount={c.productCount} />
      )}
      renderListRow={(c) => (
        <CategoryExplorerListRow category={c} productCount={c.productCount} />
      )}
      onClearAllExtra={state.clearTags}
      filterSidebarExtra={
        <Accordion defaultExpanded elevation={0} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="body2" fontWeight={600}>
              Tags
              {state.tags.length > 0 ? ` (${state.tags.length} selected)` : ""}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, maxHeight: 220, overflowY: "auto" }}>
            {tagFilterOptions.map((tag) => (
              <FormControlLabel
                key={tag}
                control={
                  <Checkbox
                    size="small"
                    checked={state.tags.includes(tag)}
                    onChange={() => state.toggleTag(tag)}
                  />
                }
                label={<Typography variant="body2">{tag}</Typography>}
              />
            ))}
            {tagFilterOptions.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No tags found on categories.
              </Typography>
            )}
            {state.tags.length > 0 && (
              <Button
                size="small"
                onClick={state.clearTags}
                sx={{ mt: 1, textTransform: "none" }}
              >
                Clear tags
              </Button>
            )}
          </AccordionDetails>
        </Accordion>
      }
    />
  );
};

export default CategoryExplorerPage;
